import React from 'react';
import { FileText, Download, Share2, MoreVertical, Folder } from 'lucide-react';
import { DataRoomFile, DataRoomFolder } from '../../types/dataRoom';
import { formatBytes, formatDate } from '../../utils/formatters';

interface DocumentListProps {
  folders: DataRoomFolder[];
  documents: DataRoomFile[];
  onFolderClick: (folder: DataRoomFolder) => void;
  onSelect: (document: DataRoomFile) => void;
}

export default function DocumentList({ folders, documents, onFolderClick, onSelect }: DocumentListProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Modified</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {/* Folders */}
          {folders.map((folder) => (
            <tr key={folder.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onFolderClick(folder)}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <Folder className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{folder.name}</p>
                    <p className="text-sm text-gray-500">{folder.files?.length || 0} files</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                  Folder
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                -
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                -
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                -
              </td>
            </tr>
          ))}

          {/* Files */}
          {documents.map((doc) => (
            <tr key={doc.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                    <p className="text-sm text-gray-500">Version {doc.version}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  doc.status === 'approved' 
                    ? 'bg-green-100 text-green-800'
                    : doc.status === 'review'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(doc.lastModified)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatBytes(doc.size)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-3">
                  {doc.permissions.download.length > 0 && (
                    <button className="text-gray-400 hover:text-gray-600">
                      <Download className="w-5 h-5" />
                    </button>
                  )}
                  <button className="text-gray-400 hover:text-gray-600">
                    <Share2 className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => onSelect(doc)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}

          {folders.length === 0 && documents.length === 0 && (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                No files or folders found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}